import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { canAccessPath, getCurrentAppUser } from '../utils/accessControl';
import './JobCards.css';

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getRangeDates = (preset) => {
  if (preset === 'all') {
    return {
      fromDate: '',
      toDate: '',
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const from = new Date(today);
  const to = new Date(today);

  if (preset === 'today') {
    return {
      fromDate: formatDateInput(from),
      toDate: formatDateInput(to),
    };
  }

  if (preset === 'week') {
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    from.setDate(today.getDate() + mondayOffset);
    return {
      fromDate: formatDateInput(from),
      toDate: formatDateInput(to),
    };
  }

  if (preset === 'month') {
    from.setDate(1);
    return {
      fromDate: formatDateInput(from),
      toDate: formatDateInput(to),
    };
  }

  from.setDate(today.getDate() - 29);
  return {
    fromDate: formatDateInput(from),
    toDate: formatDateInput(to),
  };
};

function JobCards({ onLogout }) {
  const navigate = useNavigate();
  const canCreateJobCard = canAccessPath('/jobcards/new', getCurrentAppUser());
  const [activeView, setActiveView] = useState('grid');
  const [rangePreset, setRangePreset] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [jobCards, setJobCards] = useState([]);
  const [runningLabor, setRunningLabor] = useState([]);
  const [runningLaborUpdatedAt, setRunningLaborUpdatedAt] = useState(null);
  const [error, setError] = useState('');
  const [workflowError, setWorkflowError] = useState('');
  const [closingId, setClosingId] = useState(null);
  const [clockTick, setClockTick] = useState(Date.now());
  const [fromDate, setFromDate] = useState(() => getRangeDates('all').fromDate);
  const [toDate, setToDate] = useState(() => getRangeDates('all').toDate);

  const fetchJobCards = useCallback(async () => {
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/jobcards', {
        params: {
          search: searchTerm,
          documentType: 'JobCard',
          fromDate,
          toDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setJobCards(response.data.jobCards || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load job cards.');
    }
  }, [fromDate, searchTerm, toDate]);

  useEffect(() => {
    fetchJobCards();
  }, [fetchJobCards]);

  const handleRangePresetChange = (preset) => {
    setRangePreset(preset);
    if (preset === 'custom') {
      return;
    }

    const nextRange = getRangeDates(preset);
    setFromDate(nextRange.fromDate);
    setToDate(nextRange.toDate);
  };

  const fetchRunningLabor = useCallback(async () => {
    setWorkflowError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/jobcards/running-labor', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setRunningLabor((response.data.runningLabor || []).map((item) => ({
        ...item,
        TimerFetchedAtMs: Date.now(),
      })));
      setRunningLaborUpdatedAt(new Date());
    } catch (err) {
      setWorkflowError(err.response?.data?.message || 'Failed to load running labor.');
    }
  }, []);

  useEffect(() => {
    if (activeView !== 'workflow') {
      return undefined;
    }

    fetchRunningLabor();
    const intervalId = window.setInterval(fetchRunningLabor, 10000);
    return () => window.clearInterval(intervalId);
  }, [activeView, fetchRunningLabor]);

  useEffect(() => {
    if (activeView !== 'workflow') {
      return undefined;
    }

    const intervalId = window.setInterval(() => setClockTick(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [activeView]);

  const formatCurrency = (amount) => `Tk ${Number(amount || 0).toFixed(2)}`;

  const visibleJobCards = useMemo(() => jobCards, [jobCards]);

  const formatRunningTime = (item) => {
    const baseSeconds = Math.max(0, Number(item?.RunningSeconds || 0));
    const fetchedAtMs = Number(item?.TimerFetchedAtMs || 0);
    const elapsedSinceFetch = fetchedAtMs > 0
      ? Math.max(0, Math.floor((clockTick - fetchedAtMs) / 1000))
      : 0;
    const started = item?.StartedAt ? new Date(item.StartedAt).getTime() : NaN;
    const timestampSeconds = Number.isFinite(started)
      ? Math.max(0, Math.floor((clockTick - started) / 1000))
      : 0;
    const totalSeconds = Math.max(
      baseSeconds + elapsedSinceFetch,
      timestampSeconds,
      Math.floor(Number(item?.RunningMinutes || 0) * 60),
    );
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const formatStartedAt = (startedAt) => {
    if (!startedAt) {
      return '-';
    }

    return new Date(startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getRunningJobText = (item) => {
    const names = String(item.JobNames || '').trim();
    const descriptions = String(item.JobDescriptions || '').trim();

    if (names) {
      return names;
    }

    if (descriptions) {
      return descriptions;
    }

    return 'Labor in progress';
  };

  const closeJobCard = async (jobCardId) => {
    setClosingId(jobCardId);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/jobcards/${jobCardId}/close`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      await fetchJobCards();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to close job card.');
    } finally {
      setClosingId(null);
    }
  };

  return (
    <div className="home-container app-shell-fixed">
      <Sidebar onLogout={onLogout} />
      <main className="jobcards-page app-fixed-main app-scroll-layout">
        <header className="jobcards-header app-page-header">
          <div className="jobcards-title-group app-title-group">
            <button className="app-back-button" onClick={() => navigate('/')} aria-label="Back to home" />
            <div className="app-title-copy">
              <h1>Job Cards</h1>
              <p>Track service work, invoices, bays, and status.</p>
            </div>
          </div>
          <div className="jobcards-header-actions app-header-actions">
            <button className="jobcards-outline-button" type="button" onClick={fetchJobCards}>
              Refresh
              <span className="refresh-icon" aria-hidden="true" />
            </button>
            {canCreateJobCard && (
              <button className="jobcards-new-button" type="button" onClick={() => navigate('/jobcards/new')}>
                <span aria-hidden="true">+</span>
                New Job Card
              </button>
            )}
          </div>
        </header>

        <div className="jobcards-view-switch" role="tablist" aria-label="Job card view">
          <button
            className={activeView === 'grid' ? 'active' : ''}
            type="button"
            onClick={() => setActiveView('grid')}
          >
            Grid View
          </button>
          <button
            className={activeView === 'workflow' ? 'active' : ''}
            type="button"
            onClick={() => setActiveView('workflow')}
          >
            Work Flow
            
          </button>
        </div>

        <section className="jobcards-toolbar" aria-label="Job cards filters">
          <select value={rangePreset} onChange={(event) => handleRangePresetChange(event.target.value)} aria-label="Date range">
            <option value="all">All Job Cards</option>
            <option value="today">Today</option>
            <option value="last30">Last 30 Days</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="custom">Custom</option>
          </select>
          <input
            type="date"
            value={fromDate}
            onChange={(event) => {
              setRangePreset('custom');
              setFromDate(event.target.value);
            }}
            aria-label="Start date"
          />
          <input
            type="date"
            value={toDate}
            onChange={(event) => {
              setRangePreset('custom');
              setToDate(event.target.value);
            }}
            aria-label="End date"
          />
          <label className="jobcards-search">
            <span aria-hidden="true" />
            <input
              type="search"
              placeholder="Search by name, number, status"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </label>
        </section>

        {activeView === 'workflow' ? (
          <section className="jobcards-workflow-shell" aria-live="polite">
            <div className="workflow-dashboard-header">
              <div>
                <p>Workshop Live Monitor</p>
                <h2>Running Labor</h2>
              </div>
              <div className="workflow-live-tools">
                <span className="workflow-live-badge">Live</span>
                <span>
                  {runningLaborUpdatedAt
                    ? `Updated ${runningLaborUpdatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                    : 'Waiting for update'}
                </span>
                <button type="button" onClick={fetchRunningLabor}>Refresh</button>
              </div>
            </div>

            <div className="workflow-stat-strip">
              <div>
                <span>Running jobs</span>
                <strong>{new Set(runningLabor.map((item) => item.JobCardId)).size}</strong>
              </div>
              <div>
                <span>Employees active</span>
                <strong>{new Set(runningLabor.map((item) => item.EmployeeId)).size}</strong>
              </div>
              <div>
                <span>Timers running</span>
                <strong>{runningLabor.length}</strong>
              </div>
            </div>

            {workflowError && <div className="jobcards-error">{workflowError}</div>}

            {runningLabor.length === 0 && !workflowError ? (
              <div className="workflow-empty">
                <h3>No labor timers running</h3>
                <p>When workshop employees start labor from a job card, they will appear here automatically.</p>
              </div>
            ) : (
              <div className="workflow-labor-grid">
                {runningLabor.map((item, index) => (
                  <article className={`workflow-labor-card tone-${index % 5}`} key={item.JobCardTimeLogId}>
                    <div className="workflow-card-topline">
                      <span>Job #{item.JobNumber || item.JobCardId}</span>
                      <strong>{formatRunningTime(item)}</strong>
                    </div>
                    <h3>{item.EmployeeName || 'Unassigned employee'}</h3>
                    <p className="workflow-role">{item.RoleName || 'Workshop employee'}</p>
                    <div className="workflow-car-box">
                      <span>{item.CarNumber || 'No plate'}</span>
                      <strong>{[item.Year, item.Make, item.Model].filter(Boolean).join(' ') || 'Vehicle not set'}</strong>
                    </div>
                    <div className="workflow-job-box">
                      <span>Running work</span>
                      <strong>{getRunningJobText(item)}</strong>
                    </div>
                    <dl className="workflow-card-meta">
                      <div>
                        <dt>Client</dt>
                        <dd>{item.ClientName || '-'}</dd>
                      </div>
                      <div>
                        <dt>Started</dt>
                        <dd>{formatStartedAt(item.StartedAt)}</dd>
                      </div>
                      <div>
                        <dt>Status</dt>
                        <dd>{item.JobStatus || '-'}</dd>
                      </div>
                      <div>
                        <dt>Team on car</dt>
                        <dd>{item.ActiveEmployeeCount || 1}</dd>
                      </div>
                    </dl>
                    <button type="button" onClick={() => navigate(`/jobcards/${item.JobCardId}/edit`)}>
                      View Job Card
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>
        ) : (
        <section className="jobcards-grid-shell app-scroll-results">
          <div className="jobcards-table-scroll app-grid-scroll">
            <table className="jobcards-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Service Engineer</th>
                  <th>Inv. Status</th>
                  <th>Job Status</th>
                  <th>Invoice Date</th>
                  <th>Vehicle</th>
                  <th>Plate</th>
                  <th>Bay</th>
                  <th>Total</th>
                  <th>PaidAmount</th>
                  <th>BalanceDue</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibleJobCards.map((jobCard) => (
                  <tr key={jobCard.JobCardId}>
                    <td>{jobCard.JobNumber}</td>
                    <td>{jobCard.ClientName}</td>
                    <td>{jobCard.ServiceEngineerName || '-'}</td>
                    <td>{jobCard.InvoiceStatus}</td>
                    <td>{jobCard.Status}</td>
                    <td>{jobCard.CreatedDate ? new Date(jobCard.CreatedDate).toLocaleDateString() : '-'}</td>
                    <td>{[jobCard.Year, jobCard.Make, jobCard.Model].filter(Boolean).join(' ') || '-'}</td>
                    <td>{jobCard.CarNumber || '-'}</td>
                    <td>
                      {jobCard.BayNumber ? (
                        <span className="jobcards-bay-cell">
                          Bay {String(jobCard.BayNumber).padStart(2, '0')}
                          <small>{jobCard.BayType || jobCard.BayName || 'General'}</small>
                        </span>
                      ) : '-'}
                    </td>
                    <td>{formatCurrency(jobCard.TotalAmount)}</td>
                    <td>{formatCurrency(jobCard.PaidAmount)}</td>
                    <td>{formatCurrency(jobCard.BalanceDue)}</td>
                    <td>
                      <div className="jobcards-row-actions">
                        <button
                          type="button"
                          onClick={() => navigate(`/jobcards/${jobCard.JobCardId}/edit`)}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(`/jobcards/${jobCard.JobCardId}/edit`)}
                          disabled={jobCard.Status === 'Closed'}
                        >
                          {jobCard.Status === 'Completed' ? 'View' : 'Edit'}
                        </button>
<button
  type="button"
  className="close"
  onClick={() => {
    if (window.confirm('Are you sure you want to close this job card?')) {
      closeJobCard(jobCard.JobCardId);
    }
  }}
  disabled={['Closed', 'Completed'].includes(jobCard.Status) || Number(jobCard.ActiveTimers || 0) > 0 || closingId === jobCard.JobCardId}
  title={Number(jobCard.ActiveTimers || 0) > 0 ? 'Finish active labor timers before closing this job card.' : ''}
>
  {closingId === jobCard.JobCardId ? 'Closing...' : 'Close'}
</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        )}

        {error && <div className="jobcards-error">{error}</div>}

        {activeView === 'grid' && visibleJobCards.length === 0 && !error && <section className="jobcards-empty">
          <div className="empty-jobcard-art" aria-hidden="true">
            <div className="empty-page-corner" />
            <div className="empty-face">
              <span />
              <span />
              <i />
            </div>
            <div className="empty-handle" />
          </div>
          <h2>No Job Cards</h2>
          <p>
            A job card is a detailed description of work that is performed by you or your technicians.
            You can assign work to your employees, track the progress, and even generate estimates and
            invoices from your job cards. Press the (+) button to create a new job card
          </p>
        </section>}

      </main>
    </div>
  );
}

export default JobCards;
